import React, { useState, useEffect } from 'react';
import { EmailContentTypeBodyProps } from "./EmailContentTypeBody.props";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Chip
} from "@mui/material";
import './EmailContentTypeBody.css';

const EmailContentTypeBody: React.FC<EmailContentTypeBodyProps> = ({ cognitiveBias, themes }) => {
  const [tabIndex, setTabIndex] = React.useState(0);

  const [hasShowAllFavourableBias, setHasShowAllFavourableBias] = useState(false);
  const [hasShowAllUnfavourableBias, setHasShowAllUnfavourableBias] = useState(false);

  const [hasShowAllFavourableTheme, setHasShowAllFavourableTheme] = useState(false);
  const [hasShowAllUnfavourableTheme, setHasShowAllUnfavourableTheme] = useState(false);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  // For Bias 
  const [favourableBiases, setFavourableBiases] = useState<string[]>([]);
  const [unfavourableBiases, setUnfavourableBiases] = useState<string[]>([]);

  useEffect(() => {
    if(!cognitiveBias) {
      setFavourableBiases([])
      setUnfavourableBiases([])
      return
    }

    setFavourableBiases(cognitiveBias.favourable)
    setUnfavourableBiases(cognitiveBias.unfavourable)
  }, [cognitiveBias])

  let visibleFavourableBiases;
  if(hasShowAllFavourableBias) {
    visibleFavourableBiases = favourableBiases
  } else {
    if(favourableBiases) {
      visibleFavourableBiases = favourableBiases.slice(0, 11);
    }
  }

  let visibleUnfavourableBiases;
  if(hasShowAllUnfavourableBias) {
    visibleUnfavourableBiases = unfavourableBiases
  } else {
    if(unfavourableBiases) {
      visibleUnfavourableBiases = unfavourableBiases.slice(0, 11);
    }
  }

  // For theme
  const [favourableThemes, setFavourableThemes] = useState<string[]>([]);
  const [unfavourableThemes, setUnfavourableThemes] = useState<string[]>([]);

  useEffect(() => {
    if(!themes) {
      setFavourableThemes([])
      setUnfavourableThemes([])
      return
    }

    setFavourableThemes(themes.favourable)
    setUnfavourableThemes(themes.unfavourable)
  }, [themes])

  let visibleFavourableThemes;
  if(hasShowAllFavourableTheme) {
    visibleFavourableThemes = favourableThemes
  } else {
    if(favourableThemes) {
      visibleFavourableThemes = favourableThemes.slice(0, 11);
    }
  }

  let visibleUnfavourableThemes;
  if(hasShowAllUnfavourableTheme) {
    visibleUnfavourableThemes = unfavourableThemes
  } else {
    if(unfavourableThemes) {
      visibleUnfavourableThemes = unfavourableThemes.slice(0, 11);
    }
  }

  return (
    <Box className="email-content-type-body-container">
      <Tabs
        value={tabIndex}
        onChange={handleTabChange}
        centered
        TabIndicatorProps={{ style: { display: 'none' } }}
      >
        <Tab className={ tabIndex === 0 ? 'cognitive-bias-tab-active' : 'cognitive-bias-tab'} label="Cognitive Bias" />
        <Tab className={ tabIndex === 1 ? 'themes-tab-active' : 'themes-tab'} label="Themes" />
      </Tabs>

      {/* Tab Panel Content */}
      {tabIndex === 0 && (
        <Box className='bias-content' mt={3} gap={4}>

          {/* Favourable Column */}
          <Box flex={1}>
            <Typography className="bias-section-title">Favourable</Typography>
            <Box className="bias-chip-group">
              {visibleFavourableBiases && visibleFavourableBiases.map((bias, idx) => (
                <Chip
                  key={idx}
                  label={bias}
                  variant="outlined"
                  className='favourable-item'
                />
              ))}

              {favourableBiases && favourableBiases.length > 11 && (
                <Typography
                  className='view-more-less'
                  onClick={() => setHasShowAllFavourableBias(prev => !prev)}
                >
                  {hasShowAllFavourableBias ? 'View Less' : 'View More'}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Unfavourable Column */}
          <Box flex={1}>
            <Typography className="bias-section-title">Unfavourable</Typography>

            <Box className="bias-chip-group">
              {visibleUnfavourableBiases && visibleUnfavourableBiases.map((bias, idx) => (
                <Chip
                  key={idx}
                  label={bias}
                  variant="outlined"
                  className='unfavourable-item'
                />
              ))}

              {unfavourableBiases && unfavourableBiases.length > 11 && (
                <Typography
                  className='view-more-less'
                  onClick={() => setHasShowAllUnfavourableBias(prev => !prev)}
                >
                  {hasShowAllUnfavourableBias ? 'View Less' : 'View More'}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      )}

      {/* Themes Tab */}
      {tabIndex === 1 && (
        <Box mt={3} display="flex" justifyContent="space-between" gap={4}>

          {/* Favourable Column */}
          <Box flex={1}>
            <Typography className="bias-section-title">Favourable</Typography>
            <Box className="bias-chip-group">
              {visibleFavourableThemes && visibleFavourableThemes.map((theme, idx) => (
                <Chip
                  key={idx}
                  label={theme}
                  variant="outlined"
                  className='favourable-item'
                />
              ))}

              {favourableThemes && favourableThemes.length > 11 && (
                <Typography
                  className='view-more-less'
                  onClick={() => setHasShowAllFavourableTheme(prev => !prev)}
                  sx={{ cursor: 'pointer', color: '#193051', fontWeight: 500 }}
                >
                  {hasShowAllFavourableTheme ? 'View Less' : 'View More'}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Unfavourable Column */}
          <Box flex={1}>
            <Typography className="bias-section-title">Unfavourable</Typography>

            <Box className="bias-chip-group">
              {visibleUnfavourableThemes && visibleUnfavourableThemes.map((theme, idx) => (
                <Chip
                  key={idx}
                  label={theme}
                  variant="outlined"
                  className='unfavourable-item'
                />
              ))}

              {unfavourableThemes && unfavourableThemes.length > 11 && (
                <Typography
                  className='view-more-less'
                  onClick={() => setHasShowAllUnfavourableTheme(prev => !prev)}
                  sx={{ cursor: 'pointer', color: '#193051', fontWeight: 500 }}
                >
                  {hasShowAllUnfavourableTheme ? 'View Less' : 'View More'}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default EmailContentTypeBody;
